"use client";
import cn from "@/utils/cn";
import Image from "next/image";
import React from "react";
import inputCheckIconSvg from "@/assets/icons/input-check-icon.svg";
import { usePathname } from "next/navigation";
import TextComponent from "@/app/_components/TextComponent";

const data = [
  {
    section: "Audit and Reporting Tool",
    items: [
      {
        name: "Email Audits",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Report Templates",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Notifications",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "PDF Report Export and Share",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Scheduled Reports",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Industry Benchmarks",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Status Reports and Issues",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Suggestions",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "AI Assistance",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Custom Report Editor",
        basic: "absent",
        standard: "present",
        premium: "present",
      },
    ],
  },
  {
    section: "Dashboard Builder Tool",
    items: [
      {
        name: "Dashboard Templates",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Filtering and Sorting Options",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "White-label Personalization",
        basic: "absent",
        standard: "present",
        premium: "present",
      },
      {
        name: "Custom Dashboard Editor",
        basic: "absent",
        standard: "present",
        premium: "present",
      },
    ],
  },
  {
    section: "Team Collaboration",
    items: [
      {
        name: "Accounts",
        basic: "1",
        standard: "3",
        premium: "Unlimited",
      },
      {
        name: "Users",
        basic: "1",
        standard: "10",
        premium: "Unlimited",
      },
      {
        name: "Team Collaboration",
        basic: "absent",
        standard: "present",
        premium: "present",
      },
      {
        name: "Connected Account Access",
        basic: "absent",
        standard: "present",
        premium: "present",
      },
    ],
  },
  {
    section: "Support",
    items: [
      {
        name: "Email Support",
        basic: "present",
        standard: "present",
        premium: "present",
      },
      {
        name: "Knowledge Base",
        basic: "present",
        standard: "present",
        premium: "present",
      },
    ],
  },
  {
    section: "Embeddable Audit Tool",
    items: [
      {
        name: "Embedded Audit Forms",
        basic: "absent",
        standard: "absent",
        premium: "present",
      },
      {
        name: "Generate Leads",
        basic: "absent",
        standard: "absent",
        premium: "present",
      },
      {
        name: "Call To Action Buttons",
        basic: "absent",
        standard: "absent",
        premium: "present",
      },
      {
        name: "Leads Filtering and Sorting Options",
        basic: "absent",
        standard: "absent",
        premium: "present",
      },
    ],
  },
];

const FeatureMatrixComponent = ({
  selectedCardIndex,
  className = "",
}: {
  selectedCardIndex?: number;
  className?: string;
}) => {
  const pathname = usePathname();
  const isDashboard = pathname.includes("/dashboard");

  const titleClassName =
    "text-[12px] md:text-[12px] xl:text-[20px] leading-[20.5px] md:leading-[20.5px] xl:leading-[27px] font-semibold ";
  const commonTextClassName =
    "text-[9px] md:text-[10.8px] xl:text-[14px] leading-[18px] md:leading-[18px] xl:leading-[24px]";

  return (
    <div>
      <div
        className={cn(
          "mx-auto max-w-[1120px] px-[39px] py-[78px] text-kd-gray-text md:px-[36px] lg:px-20 xl:px-0",
          isDashboard ? "hidden xl:flex" : "",
        )}
      >
        <div className="w-full rounded-[20px] border-[0.75px] border-[#EDF2F6] pb-[23px] pt-0.5">
          <table className="w-full rounded-[20px]">
            <thead className="">
              <tr className="text-[8px] text-kd-gray-text md:text-[12px] xl:text-[20px] ">
                <th
                  className={cn(
                    "w-2/3 md:px-[20px] py-[23.25px] xl:px-[27px] xl:py-[31px] px-[14px] text-left text-kd-lte-blue font-semibold",
                    // titleClassName
                  )}
                >
                  Whats under the hood
                </th>
                <th className="border-l border-[#EDF2F6] px-[14px] py-[23.25px] text-center font-semibold md:px-[20px] xl:px-[27px] xl:py-[31px]">
                  Basic
                </th>
                <th className="border-l border-[#EDF2F6] px-[14px] py-[23.25px] text-center font-semibold md:px-[20px] xl:px-[27px] xl:py-[31px]">
                  Standard
                </th>
                <th className="border-l border-[#EDF2F6] px-[14px] py-[23.25px] text-center font-semibold md:px-[20px] xl:px-[27px] xl:py-[31px]">
                  Premium
                </th>
              </tr>
            </thead>
            <tbody className={cn("", commonTextClassName)}>
              {data.map((section, index) => (
                <React.Fragment key={index}>
                  <tr className="bg-[#FAFBFC]">
                    <td className="border-l border-[#EDF2F6] px-[14px] py-2 font-semibold md:px-[20px] xl:px-[27px]">
                      {section.section}
                    </td>
                    <td className="border-l border-[#EDF2F6] px-[14px] md:px-[20px] xl:px-[27px]"></td>
                    <td className="border-l border-[#EDF2F6] px-[14px] md:px-[20px] xl:px-[27px]"></td>
                    <td className="border-l border-[#EDF2F6] px-[14px] md:px-[20px] xl:px-[27px]"></td>
                  </tr>
                  {section.items.map((item, itemIndex) => (
                    <tr key={itemIndex} className="">
                      <td className=" border-l border-[#EDF2F6] px-[14px] py-[7px] md:px-[20px] xl:px-[27px]">
                        {item.name}
                      </td>
                      <td className="border-l border-[#EDF2F6] px-[14px] py-[7px] text-center text-[10.5px] font-medium text-kd-lte-blue md:px-[20px] xl:px-[27px] xl:text-[14px]">
                        {item.basic == "present" ? (
                          <Tick />
                        ) : item.basic == "absent" ? (
                          <Cross />
                        ) : (
                          item.basic
                        )}
                      </td>
                      <td className="border-l border-[#EDF2F6] px-[14px] py-[7px] text-center text-[10.5px] font-medium text-kd-lte-blue md:px-[20px] xl:px-[27px] xl:text-[14px]">
                        {item.standard == "present" ? (
                          <Tick />
                        ) : item.standard == "absent" ? (
                          <Cross />
                        ) : (
                          item.standard
                        )}
                      </td>
                      <td className="border-l border-[#EDF2F6] px-[14px] py-[7px] text-center text-[10.5px] font-medium text-kd-lte-blue md:px-[20px] xl:px-[27px] xl:text-[14px]">
                        {item.premium == "present" ? (
                          <Tick />
                        ) : item.premium == "absent" ? (
                          <Cross />
                        ) : (
                          item.premium
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isDashboard && (
        <FeatureMatrixMobileAndTablet
          className="xl:hidden"
          selectedCardIndex={selectedCardIndex}
        />
      )}
    </div>
  );
};

const basicFeaturesData = data
  .map((section) => ({
    ...section,
    items: section.items.filter((item) => item.basic !== "absent"), // Keep if not 'absent'
  }))
  .filter((section) => section.items.length > 0); // Only keep sections with basic items

const FeatureMatrixMobileAndTablet = ({
  selectedCardIndex,
  className = "",
}: {
  selectedCardIndex?: number;
  className?: string;
}) => {
  const sectionTitleClassName =
    "text-base font-medium text-gray-800 mt-6 mb-3 pt-4 border-t border-gray-200";
  const itemClassName = "text-sm text-gray-600 mb-2";

  const selectedCardType =
    selectedCardIndex === 0
      ? "basic"
      : selectedCardIndex === 1
        ? "standard"
        : "premium";

  return (
    <div
      className={cn(
        "w-[203px] rounded-[15px] border-[0.75px] border-kd-lte-blue bg-white py-[20px]",
        className,
      )}
    >
      <TextComponent
        type="body"
        className="mb-2 px-[25px]  font-semibold text-kd-lte-blue"
      >
        Basic Features
      </TextComponent>

      <div className="">
        {data.map((item, index) => {
          const isSectionHidden =
            (selectedCardIndex === 0 && (index === 2 || index === 4)) ||
            (selectedCardIndex === 1 && index === 4);

          return (
            <div className="" key={index.toString()}>
              <TextComponent
                type="h4"
                className={cn(
                  "my-4 bg-[#FAFBFC] px-[25px] py-2 text-kd-gray-text",
                  isSectionHidden && "hidden",
                )}
              >
                {item.section}
              </TextComponent>
              <div className="space-y-2">
                {item.items.map((item, ind) => {
                  return (
                    <div className="px-[25px]" key={ind.toString()}>
                      <TextComponent
                        type="h4"
                        className={cn(
                          "font-normal leading-[18px] text-kd-gray-text",
                          item?.[selectedCardType] !== "present" && "hidden",
                        )}
                      >
                        {item.name}
                      </TextComponent>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
  return (
    <div className={cn("mx-auto max-w-md px-4 py-8 md:max-w-lg", className)}>
      {" "}
      {/* Show only on mobile/tablet */}
      <div className="rounded-xl border border-blue-200 bg-white p-6 shadow-sm">
        <TextComponent type="h4" className="">
          Basic Features
        </TextComponent>

        {basicFeaturesData.map((section, index) => (
          <div key={index}>
            {/* Add top border to all sections except the first one */}
            <h3
              className={cn(
                sectionTitleClassName,
                index === 0 && "border-t-0 pt-0 mt-0",
              )}
            >
              {section.section}
            </h3>
            <ul>
              {section.items.map((item, itemIndex) => (
                <li key={itemIndex} className={itemClassName}>
                  {/* You might want to add checkmarks here if needed,
                      but the image only shows text */}
                  {/* Example checkmark using Tick component (optional) */}
                  {/* <Tick className="inline-block mr-2" /> */}
                  {item.name}
                  {/* Display value if it's numeric (like for Accounts/Users) */}
                  {item.basic !== "present" &&
                    item.basic !== "absent" &&
                    ` (${item.basic})`}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeatureMatrixComponent;

const Tick = ({ className = "" }) => {
  return (
    <div className={cn("flex justify-center items-center", className)}>
      <div
        className={`size-3 rounded-md border-[1.4px] border-[#EDF2F6] bg-[#F9FAFB] xl:size-4`}
      >
        {<Image src={inputCheckIconSvg} alt="Checked Icon" />}
      </div>
    </div>
  );
};

const Cross = ({ className = "" }) => {
  return (
    <div className={cn("flex justify-center items-center", className)}>
      <svg
        className="flex size-[12px] items-center justify-center rounded bg-[#96A5B8] p-[3px] xl:size-[16px] xl:rounded-md xl:p-1"
        width="8"
        height="8"
        viewBox="0 0 8 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.33398 1.28784L6.59151 6.50043"
          stroke="white"
          strokeWidth="1.41451"
          strokeLinecap="round"
        />
        <path
          d="M1.33398 6.50073L6.59151 1.28814"
          stroke="white"
          strokeWidth="1.41451"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
};
